import React, { useState } from 'react';
import { Form, Row, Col, Input, Button, Alert } from 'antd';
import FormLabel from 'components/FormLabel';
import Page from 'components/Page';
import CopyToClipboardButton from 'components/CopyToClipboardButton';
import useDataSource from 'hooks/useDataSource';
import queryString from 'query-string';
import LicenseTag from 'components/LicenseTag';

const CampaignUrlBuilder = () => {
  const { dataSource, loading, getDataSourceLicenseSetting } = useDataSource({ extraFields: 'website{id url}' });
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [form] = Form.useForm();
  const hasAdvancedCampaignParameters = getDataSourceLicenseSetting('advancedCampaignParameters');
  const hasExpertCampaignParameters = getDataSourceLicenseSetting('expertCampaignParameters');

  const urlValidator = async (rules, value) => {
    if (value) {
      if (!value.startsWith(dataSource?.website.url)) {
        throw new Error();
      }
    }
  };

  const onFinish = (values) => {
    const { baseUrl, ...query } = values;
    const url = queryString.stringifyUrl({
      url: baseUrl, query,
    });
    setGeneratedUrl(url);
  };

  return (

    <Page loading={loading}>
      {dataSource && (
      <Page.Content title="Campaign URL Builder">
        <h3 style={{ lineHeight: '1.8' }}>
          The Campaign URL Builder tool empowers you to effortlessly create URLs tailored for Tracking Campaigns within Extellio. See the documentation about Campaign Tracking for more information. For guidance on how to track campaign links, check our
          {' '}
          <a href="https://academy.extellio.com/knowledge-base/analytics/how-to-track-campaign-links" target="_blank" rel="noopener noreferrer">
            comprehensive guide.
          </a>
        </h3>
        <h3>
          Fill in the information in the form below
        </h3>
        <Form form={form} onFinish={onFinish} layout="vertical" initialValues={{ baseUrl: dataSource?.website.url }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item name="baseUrl" label={<FormLabel label="Website URL" />} rules={[{ required: true }, { validator: urlValidator, message: 'Url must be start with the website url' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message="The URL of the page that this campaign goes to, for example https://example.com/" />
            </Col>
            <Col span={12}>
              <Form.Item name="mtm_campaign" label={<FormLabel label="Name [Obligatory]" />} rules={[{ required: true, message: 'Please enter campaign name' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message=" A descriptive name for the campaign, e.g. a blog post title or email campaign name." />
            </Col>
            <Col span={12}>
              <Form.Item name="mtm_source" label={<FormLabel label="Source [Recommended]" />}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message=" Enter the actual source of the traffic, such as newsletter, Twitter, eBay, LinkedIn etc." />
            </Col>
            <Col span={12}>
              <Form.Item name="mtm_medium" label={<FormLabel label="Medium [Recommended]" extra={!hasAdvancedCampaignParameters && <LicenseTag setting="advancedCampaignParameters" showTooltip />} />}>
                <Input disabled={!hasAdvancedCampaignParameters} />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message=" The type of marketing channel for your campaign, such as email, social media, paid search, etc." />
            </Col>
            <Col span={12}>
              <Form.Item name="mtm_kwd" label={<FormLabel label="Keyword" extra={!hasAdvancedCampaignParameters && <LicenseTag setting="advancedCampaignParameters" showTooltip />} />}>
                <Input disabled={!hasAdvancedCampaignParameters} />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message=" Specify the keyword that someone searched for or the category of interest associated with your campaign." />
            </Col>
            <Col span={12}>
              <Form.Item name="mtm_content" label={<FormLabel label="Content" extra={!hasExpertCampaignParameters && <LicenseTag setting="expertCampaignParameters" showTooltip />} />}>
                <Input disabled={!hasExpertCampaignParameters} />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message="Specify the content or specific link associated with the campaign that users clicked, e.g., banner, big-green-button, red-button, etc." />
            </Col>
            <Col span={12}>
              <Form.Item name="mtm_cid" label={<FormLabel label="Campaign ID " extra={!hasExpertCampaignParameters && <LicenseTag setting="expertCampaignParameters" showTooltip />} />}>
                <Input disabled={!hasExpertCampaignParameters} />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message="A unique identifier for your specific ad. This parameter is often used with the numeric IDs automatically generated by advertising platforms." />
            </Col>
            <Col span={12}>
              <Form.Item name="mtm_group" label={<FormLabel label="Group" extra={!hasExpertCampaignParameters && <LicenseTag setting="expertCampaignParameters" showTooltip />} />}>
                <Input disabled={!hasExpertCampaignParameters} />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message="If your campaign has a specific target audience, such as customers or retargeting groups, please specify." />
            </Col>
            <Col span={12}>
              <Form.Item name="mtm_placement" label={<FormLabel label="Placement [Optional]" extra={!hasExpertCampaignParameters && <LicenseTag setting="expertCampaignParameters" showTooltip />} />}>
                <Input disabled={!hasExpertCampaignParameters} />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message="The placement on an advertising network e.g. newsfeed, sidebar, home-banner, etc." />
            </Col>
            <Col span={24}>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Generate URL
                </Button>
              </Form.Item>
            </Col>
          </Row>
          {generatedUrl && (
          <Form.Item label="Generated URL">
            <Input
              readOnly
              style={{ width: '95%', marginRight: '5px' }}
              value={generatedUrl}
            />
            <CopyToClipboardButton
              textToCopy={generatedUrl}
            />
          </Form.Item>
          )}
        </Form>
      </Page.Content>
      )}
    </Page>
  );
};
export default CampaignUrlBuilder;
